import type { BrowserNotificationRuntime } from './browser-notification-runtime'
import { isActiveWebPushServerState } from './web-push-state'

const INSTALLATION_ID_KEY = 'mentiss.webPush.installationId.v1'
const INSTALLATION_OWNER_KEY = 'mentiss.webPush.ownerUserId.v1'
const INSTALLATION_SESSION_KEY = 'mentiss.webPush.ownerSessionId.v1'
const INSTALLATION_BINDING_TOKEN_KEY = 'mentiss.webPush.bindingToken.v1'
const SUBSCRIPTION_GENERATION_KEY = 'mentiss.webPush.subscriptionGeneration.v1'
const ADOPTED_SUBSCRIPTION_GENERATION_KEY
  = 'mentiss.webPush.adoptedSubscriptionGeneration.v1'
const ADOPTED_SUBSCRIPTION_ENDPOINT_FINGERPRINT_KEY
  = 'mentiss.webPush.adoptedSubscriptionEndpointFingerprint.v1'
export const WEB_PUSH_IDENTITY_REVISION_STORAGE_KEY
  = 'mentiss.webPush.identityRevision.v1'

const WEB_PUSH_BINDING_STORAGE_KEYS = [
  INSTALLATION_ID_KEY,
  INSTALLATION_OWNER_KEY,
  INSTALLATION_SESSION_KEY,
  INSTALLATION_BINDING_TOKEN_KEY,
  SUBSCRIPTION_GENERATION_KEY,
  ADOPTED_SUBSCRIPTION_GENERATION_KEY,
  ADOPTED_SUBSCRIPTION_ENDPOINT_FINGERPRINT_KEY,
] as const

interface InstallationStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

interface InstallationCrypto {
  randomUUID?: () => string
  getRandomValues: (array: Uint8Array) => Uint8Array
  subtle?: Pick<SubtleCrypto, 'digest'>
}

const UUID_PATTERN
  = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const SHA_256_FINGERPRINT_PATTERN = /^[0-9a-f]{64}$/

function fallbackUuid(cryptoImpl: InstallationCrypto): string {
  const bytes = cryptoImpl.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] % 0x10) + 0x40
  bytes[8] = (bytes[8] % 0x40) + 0x80
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0'))
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join(''),
  ].join('-')
}

function browserStorage(): InstallationStorage | null {
  try {
    return window.localStorage
  }
  catch {
    return null
  }
}

function freshUuid(cryptoImpl: InstallationCrypto): string {
  return cryptoImpl.randomUUID?.() ?? fallbackUuid(cryptoImpl)
}

function removeStoredWebPushBinding(storage: InstallationStorage | null) {
  for (const key of WEB_PUSH_BINDING_STORAGE_KEYS) storage?.removeItem(key)
}

function advanceIdentityRevision(
  storage: InstallationStorage | null,
  cryptoImpl: InstallationCrypto,
): string | null {
  try {
    if (!storage)
      return null
    const revision = freshUuid(cryptoImpl)
    storage.setItem(WEB_PUSH_IDENTITY_REVISION_STORAGE_KEY, revision)
    return storage.getItem(WEB_PUSH_IDENTITY_REVISION_STORAGE_KEY) === revision
      ? revision
      : null
  }
  catch {
    return null
  }
}

export function currentWebPushInstallationId(
  storage: InstallationStorage | null = browserStorage(),
): string | null {
  try {
    const value = storage?.getItem(INSTALLATION_ID_KEY) ?? null
    return value && UUID_PATTERN.test(value) ? value : null
  }
  catch {
    return null
  }
}

export function getOrCreateWebPushInstallationId(
  storage: InstallationStorage | null = browserStorage(),
  cryptoImpl: InstallationCrypto = window.crypto,
): string {
  const current = currentWebPushInstallationId(storage)
  if (current)
    return current

  const created = freshUuid(cryptoImpl)
  try {
    storage?.setItem(INSTALLATION_ID_KEY, created)
    advanceIdentityRevision(storage, cryptoImpl)
  }
  catch {}
  return created
}

export function clearWebPushInstallationId(
  storage: InstallationStorage | null = browserStorage(),
  cryptoImpl: InstallationCrypto = window.crypto,
): void {
  try {
    removeStoredWebPushBinding(storage)
    advanceIdentityRevision(storage, cryptoImpl)
  }
  catch {}
}

export function currentWebPushInstallationOwner(
  storage: InstallationStorage | null = browserStorage(),
): string | null {
  try {
    return storage?.getItem(INSTALLATION_OWNER_KEY) ?? null
  }
  catch {
    return null
  }
}

export function currentWebPushInstallationSession(
  storage: InstallationStorage | null = browserStorage(),
): string | null {
  try {
    return storage?.getItem(INSTALLATION_SESSION_KEY) ?? null
  }
  catch {
    return null
  }
}

export function currentWebPushBindingToken(
  storage: InstallationStorage | null = browserStorage(),
): string | null {
  try {
    const value = storage?.getItem(INSTALLATION_BINDING_TOKEN_KEY) ?? null
    return value && UUID_PATTERN.test(value) ? value : null
  }
  catch {
    return null
  }
}

export function currentWebPushIdentityRevision(
  storage: InstallationStorage | null = browserStorage(),
): string | null {
  try {
    return storage?.getItem(WEB_PUSH_IDENTITY_REVISION_STORAGE_KEY) ?? null
  }
  catch {
    return null
  }
}

function currentWebPushSubscriptionGeneration(
  storage: InstallationStorage | null = browserStorage(),
  key = SUBSCRIPTION_GENERATION_KEY,
): string | null {
  try {
    const value = storage?.getItem(key) ?? null
    return value && UUID_PATTERN.test(value) ? value : null
  }
  catch {
    return null
  }
}

function currentWebPushAdoptedEndpointFingerprint(
  storage: InstallationStorage | null = browserStorage(),
): string | null {
  try {
    const value
      = storage?.getItem(ADOPTED_SUBSCRIPTION_ENDPOINT_FINGERPRINT_KEY) ?? null
    return value && SHA_256_FINGERPRINT_PATTERN.test(value) ? value : null
  }
  catch {
    return null
  }
}

async function fingerprintWebPushEndpoint(
  endpoint: string,
  cryptoImpl: InstallationCrypto,
): Promise<string | null> {
  if (!cryptoImpl.subtle)
    return null
  try {
    const digest = await cryptoImpl.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(endpoint),
    )
    return Array.from(new Uint8Array(digest), byte =>
      byte.toString(16).padStart(2, '0')).join('')
  }
  catch {
    return null
  }
}

export function createWebPushBindingToken(
  cryptoImpl: InstallationCrypto = window.crypto,
): string {
  return freshUuid(cryptoImpl)
}

export interface WebPushInstallationBindingSnapshot {
  installationId: string | null
  ownerUserId: string | null
  ownerSessionId: string | null
  bindingToken: string | null
  subscriptionGeneration: string | null
  adoptedSubscriptionGeneration: string | null
  adoptedSubscriptionEndpointFingerprint: string | null
  identityRevision: string | null
}

export function captureWebPushInstallationBinding(
  storage: InstallationStorage | null = browserStorage(),
): WebPushInstallationBindingSnapshot {
  return {
    installationId: currentWebPushInstallationId(storage),
    ownerUserId: currentWebPushInstallationOwner(storage),
    ownerSessionId: currentWebPushInstallationSession(storage),
    bindingToken: currentWebPushBindingToken(storage),
    subscriptionGeneration: currentWebPushSubscriptionGeneration(storage),
    adoptedSubscriptionGeneration: currentWebPushSubscriptionGeneration(
      storage,
      ADOPTED_SUBSCRIPTION_GENERATION_KEY,
    ),
    adoptedSubscriptionEndpointFingerprint:
      currentWebPushAdoptedEndpointFingerprint(storage),
    identityRevision: currentWebPushIdentityRevision(storage),
  }
}

export function webPushInstallationBindingIsCurrent(
  snapshot: WebPushInstallationBindingSnapshot,
  storage: InstallationStorage | null = browserStorage(),
): boolean {
  const current = captureWebPushInstallationBinding(storage)
  return (
    current.installationId === snapshot.installationId
    && current.ownerUserId === snapshot.ownerUserId
    && current.ownerSessionId === snapshot.ownerSessionId
    && current.bindingToken === snapshot.bindingToken
    && current.subscriptionGeneration === snapshot.subscriptionGeneration
    && current.adoptedSubscriptionGeneration
    === snapshot.adoptedSubscriptionGeneration
    && current.adoptedSubscriptionEndpointFingerprint
    === snapshot.adoptedSubscriptionEndpointFingerprint
    && current.identityRevision === snapshot.identityRevision
  )
}

function sameWebPushBindingTuple(
  first: WebPushInstallationBindingSnapshot,
  second: WebPushInstallationBindingSnapshot,
): boolean {
  return (
    first.installationId === second.installationId
    && first.ownerUserId === second.ownerUserId
    && first.ownerSessionId === second.ownerSessionId
    && first.bindingToken === second.bindingToken
  )
}

function sameWebPushSubscriptionOwnership(
  first: WebPushInstallationBindingSnapshot,
  second: WebPushInstallationBindingSnapshot,
): boolean {
  return (
    sameWebPushBindingTuple(first, second)
    && first.subscriptionGeneration === second.subscriptionGeneration
  )
}

function hasCompleteWebPushBinding(
  binding: WebPushInstallationBindingSnapshot,
): boolean {
  return (
    !!binding.installationId
    && !!binding.ownerUserId
    && !!binding.ownerSessionId
    && !!binding.bindingToken
  )
}

function captureEmptyWebPushBindingTombstone(
  storage: InstallationStorage | null = browserStorage(),
): WebPushInstallationBindingSnapshot | null {
  try {
    if (
      !storage
      || WEB_PUSH_BINDING_STORAGE_KEYS.some(key => storage.getItem(key) !== null)
    ) {
      return null
    }
    const identityRevision = storage.getItem(
      WEB_PUSH_IDENTITY_REVISION_STORAGE_KEY,
    )
    if (!identityRevision || !UUID_PATTERN.test(identityRevision))
      return null
    return {
      installationId: null,
      ownerUserId: null,
      ownerSessionId: null,
      bindingToken: null,
      subscriptionGeneration: null,
      adoptedSubscriptionGeneration: null,
      adoptedSubscriptionEndpointFingerprint: null,
      identityRevision,
    }
  }
  catch {
    return null
  }
}

function webPushSubscriptionOwnershipIsCurrent(
  binding: WebPushInstallationBindingSnapshot,
  storage: InstallationStorage | null = browserStorage(),
): boolean {
  return sameWebPushSubscriptionOwnership(
    captureWebPushInstallationBinding(storage),
    binding,
  )
}

function lifecycleLockUnavailable(
  runtime: BrowserNotificationRuntime,
): boolean {
  return !runtime.readCapabilities().lifecycleLock
}

async function removeLocalPushWithoutLifecycleLock(
  runtime: BrowserNotificationRuntime,
): Promise<void> {
  const capabilities = runtime.readCapabilities()
  if (!capabilities.serviceWorker || !capabilities.push)
    return
  const subscription = await runtime.getPushSubscription()
  if (subscription)
    await subscription.unsubscribe()
}

async function withCleanupLifecycle<T>(
  runtime: BrowserNotificationRuntime,
  operation: (withoutLifecycleLock: boolean) => Promise<T>,
): Promise<T> {
  if (lifecycleLockUnavailable(runtime))
    return operation(true)
  return runtime.withWebPushLifecycleLock(() => operation(false))
}

export function markWebPushIdentityChanged(
  storage: InstallationStorage | null = browserStorage(),
  cryptoImpl: InstallationCrypto = window.crypto,
): boolean {
  return advanceIdentityRevision(storage, cryptoImpl) !== null
}

export function setWebPushInstallationBinding(
  expectedUserId: string,
  expectedSessionId: string,
  installationId: string,
  bindingToken: string,
  storage: InstallationStorage | null = browserStorage(),
  cryptoImpl: InstallationCrypto = window.crypto,
): boolean {
  try {
    storage?.setItem(INSTALLATION_ID_KEY, installationId)
    storage?.setItem(INSTALLATION_OWNER_KEY, expectedUserId)
    storage?.setItem(INSTALLATION_SESSION_KEY, expectedSessionId)
    storage?.setItem(INSTALLATION_BINDING_TOKEN_KEY, bindingToken)
    storage?.removeItem(SUBSCRIPTION_GENERATION_KEY)
    storage?.removeItem(ADOPTED_SUBSCRIPTION_GENERATION_KEY)
    storage?.removeItem(ADOPTED_SUBSCRIPTION_ENDPOINT_FINGERPRINT_KEY)
    const revision = advanceIdentityRevision(storage, cryptoImpl)
    const verified
      = !!storage
        && currentWebPushInstallationId(storage) === installationId
        && currentWebPushInstallationOwner(storage) === expectedUserId
        && currentWebPushInstallationSession(storage) === expectedSessionId
        && currentWebPushBindingToken(storage) === bindingToken
        && captureWebPushInstallationBinding(storage).subscriptionGeneration
        === null
        && captureWebPushInstallationBinding(storage)
          .adoptedSubscriptionGeneration === null
          && captureWebPushInstallationBinding(storage)
            .adoptedSubscriptionEndpointFingerprint === null
            && !!revision
            && currentWebPushIdentityRevision(storage) === revision
    if (verified)
      return true
    removeStoredWebPushBinding(storage)
    advanceIdentityRevision(storage, cryptoImpl)
    return false
  }
  catch {
    try {
      removeStoredWebPushBinding(storage)
      advanceIdentityRevision(storage, cryptoImpl)
    }
    catch {}
    return false
  }
}

export function beginWebPushSubscriptionActivation(
  binding: WebPushInstallationBindingSnapshot,
  storage: InstallationStorage | null = browserStorage(),
  cryptoImpl: InstallationCrypto = window.crypto,
): WebPushInstallationBindingSnapshot | null {
  if (!webPushInstallationBindingIsCurrent(binding, storage) || !storage)
    return null
  try {
    const subscriptionGeneration = freshUuid(cryptoImpl)
    storage.setItem(SUBSCRIPTION_GENERATION_KEY, subscriptionGeneration)
    storage.removeItem(ADOPTED_SUBSCRIPTION_GENERATION_KEY)
    storage.removeItem(ADOPTED_SUBSCRIPTION_ENDPOINT_FINGERPRINT_KEY)
    const identityRevision = advanceIdentityRevision(storage, cryptoImpl)
    const next = captureWebPushInstallationBinding(storage)
    if (
      !identityRevision
      || !sameWebPushBindingTuple(next, binding)
      || next.subscriptionGeneration !== subscriptionGeneration
      || next.identityRevision !== identityRevision
    ) {
      return null
    }
    return next
  }
  catch {
    return null
  }
}

export async function adoptWebPushSubscriptionActivation(
  binding: WebPushInstallationBindingSnapshot,
  endpoint: string,
  storage: InstallationStorage | null = browserStorage(),
  cryptoImpl: InstallationCrypto = window.crypto,
): Promise<WebPushInstallationBindingSnapshot | null> {
  const endpointFingerprint = await fingerprintWebPushEndpoint(
    endpoint,
    cryptoImpl,
  )
  if (
    !endpointFingerprint
    || !binding.subscriptionGeneration
    || !webPushInstallationBindingIsCurrent(binding, storage)
    || !storage
  ) {
    return null
  }
  if (
    binding.adoptedSubscriptionGeneration === binding.subscriptionGeneration
    && binding.adoptedSubscriptionEndpointFingerprint === endpointFingerprint
  ) {
    return binding
  }
  try {
    storage.setItem(
      ADOPTED_SUBSCRIPTION_GENERATION_KEY,
      binding.subscriptionGeneration,
    )
    storage.setItem(
      ADOPTED_SUBSCRIPTION_ENDPOINT_FINGERPRINT_KEY,
      endpointFingerprint,
    )
    const identityRevision = advanceIdentityRevision(storage, cryptoImpl)
    const next = captureWebPushInstallationBinding(storage)
    if (
      !identityRevision
      || !sameWebPushSubscriptionOwnership(next, binding)
      || next.adoptedSubscriptionGeneration !== binding.subscriptionGeneration
      || next.adoptedSubscriptionEndpointFingerprint !== endpointFingerprint
      || next.identityRevision !== identityRevision
    ) {
      return null
    }
    return next
  }
  catch {
    return null
  }
}

export async function confirmWebPushSubscriptionRegistration(
  binding: WebPushInstallationBindingSnapshot,
  endpoint: string,
  storage: InstallationStorage | null = browserStorage(),
  cryptoImpl: InstallationCrypto = window.crypto,
): Promise<WebPushInstallationBindingSnapshot | null> {
  if (binding.subscriptionGeneration) {
    return adoptWebPushSubscriptionActivation(
      binding,
      endpoint,
      storage,
      cryptoImpl,
    )
  }
  if (!webPushInstallationBindingIsCurrent(binding, storage))
    return null
  const revision = advanceIdentityRevision(storage, cryptoImpl)
  const next = captureWebPushInstallationBinding(storage)
  return revision && sameWebPushBindingTuple(next, binding) ? next : null
}

export function invalidateWebPushSubscriptionAdoption(
  binding: WebPushInstallationBindingSnapshot,
  storage: InstallationStorage | null = browserStorage(),
  cryptoImpl: InstallationCrypto = window.crypto,
): WebPushInstallationBindingSnapshot | null {
  if (!webPushInstallationBindingIsCurrent(binding, storage) || !storage)
    return null
  try {
    storage.removeItem(ADOPTED_SUBSCRIPTION_GENERATION_KEY)
    storage.removeItem(ADOPTED_SUBSCRIPTION_ENDPOINT_FINGERPRINT_KEY)
    const identityRevision = advanceIdentityRevision(storage, cryptoImpl)
    const next = captureWebPushInstallationBinding(storage)
    if (
      !identityRevision
      || !sameWebPushSubscriptionOwnership(next, binding)
      || next.adoptedSubscriptionGeneration !== null
      || next.adoptedSubscriptionEndpointFingerprint !== null
      || next.identityRevision !== identityRevision
    ) {
      return null
    }
    return next
  }
  catch {
    return null
  }
}

export function decodeVapidPublicKey(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - (value.length % 4)) % 4)
  const binary = atob(
    (value + padding).replaceAll('-', '+').replaceAll('_', '/'),
  )
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++)
    bytes[index] = binary.codePointAt(index) ?? 0
  return bytes.buffer
}

export interface SerializedPushSubscription {
  endpoint: string
  expirationTime: number | null
  keys: {
    p256dh: string
    auth: string
  }
}

export function serializePushSubscription(
  subscription: PushSubscription,
): SerializedPushSubscription {
  const json = subscription.toJSON()
  const p256dh = json.keys?.p256dh
  const auth = json.keys?.auth
  if (!json.endpoint || !p256dh || !auth)
    throw new Error('Browser returned an incomplete Push subscription')
  return {
    endpoint: json.endpoint,
    expirationTime: json.expirationTime ?? null,
    keys: { p256dh, auth },
  }
}

export class WebPushSubscriptionTimeoutError extends Error {
  constructor() {
    super('Service Worker readiness timed out')
    this.name = 'WebPushSubscriptionTimeoutError'
  }
}

export async function subscribeToPushWithGuardedTimeout(
  runtime: BrowserNotificationRuntime,
  applicationServerKey: BufferSource,
  binding: WebPushInstallationBindingSnapshot,
  timeoutMs: number,
): Promise<PushSubscription> {
  if (!webPushInstallationBindingIsCurrent(binding))
    throw new Error('Push subscription ownership changed')
  const pendingSubscription = runtime.subscribeToPush(applicationServerKey)
  let timeout: ReturnType<typeof setTimeout> | undefined
  let timedOut = false
  try {
    return await Promise.race([
      pendingSubscription,
      new Promise<PushSubscription>((_resolve, reject) => {
        timeout = setTimeout(() => {
          timedOut = true
          reject(new WebPushSubscriptionTimeoutError())
        }, timeoutMs)
      }),
    ])
  }
  catch (error) {
    if (timedOut) {
      void pendingSubscription
        .then(
          async (lateSubscription) => {
            await runtime.withWebPushLifecycleLock(async () => {
              const currentBinding = captureWebPushInstallationBinding()
              const completeAdoptedBinding
                = hasCompleteWebPushBinding(currentBinding)
                  && !!currentBinding.subscriptionGeneration
                  && currentBinding.adoptedSubscriptionGeneration
                  === currentBinding.subscriptionGeneration
                  && !!currentBinding.adoptedSubscriptionEndpointFingerprint
              if (completeAdoptedBinding) {
                const lateEndpointFingerprint
                  = await fingerprintWebPushEndpoint(
                    lateSubscription.endpoint,
                    window.crypto,
                  )
                if (
                  lateEndpointFingerprint
                  === currentBinding.adoptedSubscriptionEndpointFingerprint
                ) {
                  return
                }
              }

              try {
                await lateSubscription.unsubscribe()
              }
              catch {
                return
              }
              const bindingAfterUnsubscribe
                = captureWebPushInstallationBinding()
              const completeBindingAfterUnsubscribe = hasCompleteWebPushBinding(
                bindingAfterUnsubscribe,
              )
              const adopterAfterUnsubscribe
                = completeBindingAfterUnsubscribe
                  && !!bindingAfterUnsubscribe.subscriptionGeneration
                  && bindingAfterUnsubscribe.adoptedSubscriptionGeneration
                  === bindingAfterUnsubscribe.subscriptionGeneration
                  && !!bindingAfterUnsubscribe.adoptedSubscriptionEndpointFingerprint
              if (adopterAfterUnsubscribe)
                return
              try {
                await runtime.closeWebPushNotifications()
              }
              catch {}
              if (!completeBindingAfterUnsubscribe)
                clearWebPushInstallationId()
            })
          },
          async () => {
            await runtime.withWebPushLifecycleLock(async () => {
              if (
                !hasCompleteWebPushBinding(captureWebPushInstallationBinding())
              )
                clearWebPushInstallationId()
            })
          },
        )
        .catch(() => {})
    }
    throw error
  }
  finally {
    if (timeout)
      clearTimeout(timeout)
  }
}

export interface WebPushDeviceDeactivationStatus {
  subscriptionState:
    | 'unregistered'
    | 'active'
    | 'locally-disabled'
    | 'temporarily-unhealthy'
    | 'server-invalidated'
}

export interface WebPushDeviceDeactivationClient<
  T extends WebPushDeviceDeactivationStatus = WebPushDeviceDeactivationStatus,
> {
  deactivate: (input: {
    expectedUserId: string
    expectedSessionId: string
    installationId: string
    bindingToken: string
    reason: 'user_disabled'
  }) => Promise<T>
}

export interface WebPushDeviceDeactivationResult<
  T extends WebPushDeviceDeactivationStatus = WebPushDeviceDeactivationStatus,
> {
  serverResult?: T
  serverDeactivated: boolean
  localSubscriptionRemoved: boolean
  staleIdentity: boolean
}

export async function compensateFailedWebPushRegistration<
  T extends WebPushDeviceDeactivationStatus,
>(
  expectedUserId: string,
  expectedSessionId: string,
  binding: WebPushInstallationBindingSnapshot,
  client: {
    deactivate: (input: {
      expectedUserId: string
      expectedSessionId: string
      installationId: string
      bindingToken: string
      reason: 'subscription_missing'
    }) => Promise<T>
  },
  runtime: BrowserNotificationRuntime,
): Promise<{
  serverDeactivated: boolean
  localSubscriptionRemoved: boolean
  bindingCleared: boolean
}> {
  let serverDeactivated = false
  let localSubscriptionRemoved = false
  let bindingCleared = false
  if (
    !binding.installationId
    || !binding.bindingToken
    || binding.ownerUserId !== expectedUserId
    || binding.ownerSessionId !== expectedSessionId
    || !webPushSubscriptionOwnershipIsCurrent(binding)
  ) {
    return { serverDeactivated, localSubscriptionRemoved, bindingCleared }
  }

  try {
    const result = await client.deactivate({
      expectedUserId,
      expectedSessionId,
      installationId: binding.installationId,
      bindingToken: binding.bindingToken,
      reason: 'subscription_missing',
    })
    serverDeactivated = !isActiveWebPushServerState(result.subscriptionState)
  }
  catch {}
  if (!webPushSubscriptionOwnershipIsCurrent(binding))
    return { serverDeactivated, localSubscriptionRemoved, bindingCleared }

  try {
    localSubscriptionRemoved = await runtime.unsubscribeFromPush()
  }
  catch {}
  if (
    serverDeactivated
    && localSubscriptionRemoved
    && webPushSubscriptionOwnershipIsCurrent(binding)
  ) {
    clearWebPushInstallationId()
    bindingCleared = true
  }
  return { serverDeactivated, localSubscriptionRemoved, bindingCleared }
}

export async function disableWebPushCurrentDevice<
  T extends WebPushDeviceDeactivationStatus,
>(
  expectedUserId: string,
  expectedSessionId: string,
  client: WebPushDeviceDeactivationClient<T>,
  runtime: BrowserNotificationRuntime,
): Promise<WebPushDeviceDeactivationResult<T>> {
  let currentBinding = captureWebPushInstallationBinding()
  let serverResult: T | undefined
  let serverDeactivated = false
  let localSubscriptionRemoved = false
  let staleIdentity = false

  if (
    !currentBinding.installationId
    || currentBinding.ownerUserId !== expectedUserId
    || currentBinding.ownerSessionId !== expectedSessionId
    || !currentBinding.bindingToken
  ) {
    return {
      serverResult,
      serverDeactivated,
      localSubscriptionRemoved,
      staleIdentity: true,
    }
  }

  await runtime.withWebPushLifecycleLock(async () => {
    if (!webPushInstallationBindingIsCurrent(currentBinding)) {
      staleIdentity = true
      return
    }
    try {
      serverResult = await client.deactivate({
        expectedUserId,
        expectedSessionId,
        installationId: currentBinding.installationId!,
        bindingToken: currentBinding.bindingToken!,
        reason: 'user_disabled',
      })
      serverDeactivated = !isActiveWebPushServerState(
        serverResult.subscriptionState,
      )
    }
    catch {}
    if (serverResult && !serverDeactivated)
      return
    if (!webPushInstallationBindingIsCurrent(currentBinding)) {
      staleIdentity = true
      return
    }
    const invalidatedBinding
      = invalidateWebPushSubscriptionAdoption(currentBinding)
    if (!invalidatedBinding) {
      staleIdentity = true
      return
    }
    currentBinding = invalidatedBinding
    try {
      await runtime.unsubscribeFromPush()
      localSubscriptionRemoved = true
    }
    catch {}
    if (!webPushInstallationBindingIsCurrent(currentBinding)) {
      staleIdentity = true
      return
    }
    try {
      await runtime.closeWebPushNotifications()
    }
    catch {}
    if (!webPushInstallationBindingIsCurrent(currentBinding)) {
      staleIdentity = true
    }
  })

  return {
    serverResult,
    serverDeactivated,
    localSubscriptionRemoved,
    staleIdentity,
  }
}

export interface WebPushAccountExitClient {
  deactivateSession: (input: {
    expectedUserId: string
    expectedSessionId: string | null
  }) => Promise<unknown>
}

export interface WebPushAccountExitResult {
  serverDeactivated: boolean
  localSubscriptionRemoved: boolean
  safeToClearAccountIdentity: boolean
}

export async function detachWebPushBeforeAccountExit(
  expectedUserId: string,
  expectedSessionId: string | null,
  client: WebPushAccountExitClient,
  runtime: BrowserNotificationRuntime,
): Promise<WebPushAccountExitResult> {
  let serverDeactivated = false
  let localSubscriptionRemoved = false
  const queuedBinding = captureWebPushInstallationBinding()
  await withCleanupLifecycle(runtime, async (withoutLifecycleLock) => {
    const lockedBinding = captureWebPushInstallationBinding()
    const localBindingBelongsToExitingSession
      = (!lockedBinding.ownerUserId
        && !lockedBinding.ownerSessionId
        && !lockedBinding.bindingToken)
      || (lockedBinding.ownerUserId === expectedUserId
        && lockedBinding.ownerSessionId === expectedSessionId)
    const queuedIntentStillSafe
      = webPushInstallationBindingIsCurrent(queuedBinding)
        || localBindingBelongsToExitingSession
    try {
      await client.deactivateSession({ expectedUserId, expectedSessionId })
      serverDeactivated = true
    }
    catch {}

    if (
      queuedIntentStillSafe
      && localBindingBelongsToExitingSession
      && webPushInstallationBindingIsCurrent(lockedBinding)
    ) {
      try {
        await (withoutLifecycleLock ? removeLocalPushWithoutLifecycleLock(runtime) : runtime.unsubscribeFromPush())
        localSubscriptionRemoved = true
      }
      catch {}

      if (webPushInstallationBindingIsCurrent(lockedBinding)) {
        try {
          await runtime.closeWebPushNotifications()
        }
        catch {}
      }
    }

    if (
      serverDeactivated
      && localBindingBelongsToExitingSession
      && webPushInstallationBindingIsCurrent(lockedBinding)
    ) {
      clearWebPushInstallationId()
    }
  })

  return {
    serverDeactivated,
    localSubscriptionRemoved,
    safeToClearAccountIdentity: serverDeactivated,
  }
}

export async function sanitizeWebPushBeforeAccountEntry(
  runtime: BrowserNotificationRuntime,
): Promise<boolean> {
  let cleanupBinding = captureWebPushInstallationBinding()
  try {
    await withCleanupLifecycle(runtime, async (withoutLifecycleLock) => {
      if (!webPushInstallationBindingIsCurrent(cleanupBinding)) {
        const clearedBinding = captureEmptyWebPushBindingTombstone()
        if (
          withoutLifecycleLock
          || !clearedBinding
          || clearedBinding.identityRevision === cleanupBinding.identityRevision
          || !webPushInstallationBindingIsCurrent(clearedBinding)
        ) {
          throw new Error('Web Push identity changed before cleanup')
        }
        cleanupBinding = clearedBinding
      }
      await (withoutLifecycleLock
        ? removeLocalPushWithoutLifecycleLock(runtime)
        : runtime.unsubscribeFromPush())
      if (!webPushInstallationBindingIsCurrent(cleanupBinding))
        throw new Error('Web Push identity changed during cleanup')
      try {
        await runtime.closeWebPushNotifications()
      }
      catch {}
      if (!webPushInstallationBindingIsCurrent(cleanupBinding))
        throw new Error('Web Push identity changed during cleanup')
      clearWebPushInstallationId()
    })
  }
  catch {
    return false
  }
  return true
}
